import { useCallback, useContext, useEffect, useState } from "react";
import { SocketContext } from "../../../../contexts/SocketContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetWebhooksDTO from "../../../../models/DTO/IGetWebhooksDTO";
import IOnGetWebhooksDTO from "../../../../models/DTO/IOnGetWebhooksDTO";
import IWebhookSummary from "../../../../models/IWebhookSummary";

/**
 * The business's webhook endpoints over the socket. Pass `enabled` false to skip the request (the
 * business has no webhooks, or no socket is mounted, as in tests). `reload` asks the server again.
 */
export const useWebhookEndpoints = (enabled: boolean) => {
    const socket = useContext(SocketContext);
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [webhooks, setWebhooks] = useState<IWebhookSummary[]>([]);
    const [maxWebhooks, setMaxWebhooks] = useState(10);
    const [loaded, setLoaded] = useState(false);

    const reload = useCallback(() => {
        if (!enabled || !socket || !userModel || !supplierModel) {
            return;
        }

        const payload: IGetWebhooksDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.WebhooksGetWebhooks, payload);
    }, [enabled, socket, userModel, supplierModel]);

    useEffect(() => {
        if (!enabled || !socket) {
            return;
        }

        const onGetWebhooks = (data: IOnGetWebhooksDTO) => {
            setWebhooks(data.webhooks || []);
            setMaxWebhooks(data.max_webhooks || 10);
            setLoaded(true);
        };

        socket.on(EndpointsEnum.WebhooksOnGetWebhooks, onGetWebhooks);
        reload();

        return () => {
            socket.off(EndpointsEnum.WebhooksOnGetWebhooks, onGetWebhooks);
        };
    }, [enabled, socket, reload]);

    return { webhooks, maxWebhooks, loaded, reload };
};
